import { computed, inject, Injectable } from '@angular/core';
import { AuthService } from '../../../modules/auth/services/auth/auth.service';
import { MODULE_PREFIX_FEATURES } from '../../config/module-entitlements.config';

@Injectable({ providedIn: 'root' })
export class SessionEntitlementService {
  private readonly auth = inject(AuthService);

  readonly entitlementCodes = computed(() => {
    const user = this.auth.userDetails();
    if (!user) {
      return [] as string[];
    }
    const raw = user['entitlements'];
    if (!Array.isArray(raw)) {
      return [] as string[];
    }
    return raw.filter(
      (c): c is string => typeof c === 'string' && c.trim().length > 0,
    );
  });

  readonly entitlementsResolved = computed(() => {
    const user = this.auth.userDetails();
    if (!user) {
      return false;
    }
    return Array.isArray(user['entitlements']);
  });

  hasFeature(code: string): boolean {
    const required = code?.trim();
    if (!required || !this.entitlementsResolved()) {
      return false;
    }
    return this.entitlementCodes().includes(required);
  }

  hasAnyFeature(...codes: string[]): boolean {
    if (codes.length === 0) {
      return true;
    }
    if (!this.entitlementsResolved()) {
      return false;
    }
    const list = this.entitlementCodes();
    if (list.length === 0) {
      return false;
    }
    return codes.some((c) => list.includes(c));
  }

  canAccessModule(slugPrefix: string): boolean {
    const prefix = slugPrefix?.trim();
    if (!prefix) {
      return false;
    }
    const requiredFeatures = MODULE_PREFIX_FEATURES[prefix];
    if (!requiredFeatures || requiredFeatures.length === 0) {
      return true;
    }
    return this.hasAnyFeature(...requiredFeatures);
  }

  /**
   * Roles & Permissions matrix: only modules included on the active subscription plan.
   * Unlike {@link canAccessModule}, unmapped slug prefixes are hidden.
   */
  canAssignModulePermission(slugPrefix: string): boolean {
    const prefix = slugPrefix?.trim();
    if (!prefix) {
      return false;
    }
    const requiredFeatures = MODULE_PREFIX_FEATURES[prefix];
    if (!requiredFeatures || requiredFeatures.length === 0) {
      return false;
    }
    return this.hasAnyFeature(...requiredFeatures);
  }

  canAccessAnyModule(...slugPrefixes: string[]): boolean {
    if (slugPrefixes.length === 0) {
      return true;
    }
    return slugPrefixes.some((p) => this.canAccessModule(p));
  }
}
